// 空結果、錯誤、載入中的畫面。空結果時列出目前生效的條件，讓使用者一鍵放寬。
import type { LoadError } from '../../data/client.ts';
import type { ClearableFilter } from '../../filters/state.ts';
import { t } from '../../i18n/zh-TW.ts';
import { CloudOff, ICON, SearchX, WifiOff } from '../../ui/icons/index.ts';

const FILTER_LABEL: Record<ClearableFilter, string> = {
  q: t.search.label,
  platforms: t.filters.platforms,
  genres: t.filters.genres,
  date: `${t.filters.dateFrom}／${t.filters.dateTo}`,
};

export function EmptyState({
  active,
  onClear,
}: {
  active: ClearableFilter[];
  onClear: (f: ClearableFilter | 'all') => void;
}) {
  return (
    <div className="state state-empty" role="status">
      <SearchX {...ICON} className="state-icon" />
      <p className="state-title">{t.list.empty}</p>
      {active.length > 0 && (
        <>
          <p>{t.list.emptyHint}</p>
          <div className="state-actions">
            {active.map((f) => (
              <button key={f} type="button" onClick={() => onClear(f)}>
                {t.list.clearFilter(FILTER_LABEL[f])}
              </button>
            ))}
            {active.length > 1 && (
              <button type="button" className="primary" onClick={() => onClear('all')}>
                {t.list.clearAll}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function errorMessage(e: LoadError | null): string {
  switch (e?.kind) {
    case 'network':
      return t.error.network;
    case 'notFound':
      return t.error.notFound;
    case 'schema':
      return t.error.schema;
    default:
      return t.error.unknown;
  }
}

export function ErrorState({ error, onRetry }: { error: LoadError | null; onRetry: () => void }) {
  const schema = error?.kind === 'schema';
  return (
    <div className="state state-error" role="alert">
      <CloudOff {...ICON} className="state-icon" />
      <p className="state-title">{t.error.title}</p>
      <p>{errorMessage(error)}</p>
      <div className="state-actions">
        <button type="button" className="primary" onClick={schema ? () => window.location.reload() : onRetry}>
          {schema ? t.error.reload : t.error.retry}
        </button>
      </div>
    </div>
  );
}

/** 沒有網路、而且這台裝置還沒有存下資料：說明狀況，連上網路後重試 */
export function OfflineState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="state state-offline" role="alert">
      <WifiOff {...ICON} className="state-icon" />
      <p className="state-title">{t.pwa.offlineTitle}</p>
      <p>{t.pwa.offlineBody}</p>
      <div className="state-actions">
        <button type="button" className="primary" onClick={onRetry}>
          {t.error.retry}
        </button>
      </div>
    </div>
  );
}

export function ErrorBanner({ error, onRetry }: { error: LoadError; onRetry: () => void }) {
  return (
    <div className="banner banner-error" role="alert">
      <span>{errorMessage(error)}</span>
      <button type="button" onClick={onRetry}>
        {t.error.retry}
      </button>
    </div>
  );
}

export function Skeleton({ count = 6, layout = 'list' }: { count?: number; layout?: 'grid' | 'list' }) {
  if (layout === 'grid') {
    return (
      <div className="poster-grid skeleton-grid" role="status" aria-busy="true" aria-label={t.list.loading}>
        {Array.from({ length: count * 2 }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: 固定數量的佔位元素
          <div key={i} className="skeleton-poster">
            <div className="skeleton-art" />
            <div className="skeleton-line w60" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="skeleton" role="status" aria-busy="true" aria-label={t.list.loading}>
      {Array.from({ length: count }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: 固定數量的佔位元素
        <div key={i} className="card skeleton-card">
          <div className="cover" />
          <div className="card-body">
            <div className="skeleton-line w60" />
            <div className="skeleton-line w30" />
            <div className="skeleton-line w45" />
          </div>
        </div>
      ))}
    </div>
  );
}
